import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { UserCheck, Trash2, Loader2, Mail, Users, Copy, Lock, Infinity as InfinityIcon, Clock } from "lucide-react";
import { API, authHeaders, formatApiError } from "./AdminPage";
import { useAdminT } from "./adminI18n";

const inputCls =
  "w-full bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-3 focus:outline-none focus:border-luxury-gold";

const JoinLinkBox = ({ courseId, t }) => {
  if (!courseId) return null;
  const url = `${window.location.origin}/join/${courseId}`;
  return (
    <div data-testid="join-link-box" className="border border-luxury-gold/40 bg-luxury-gold/5 p-4 rounded-xl">
      <p className="text-luxury-gold text-sm font-semibold mb-1">{t("رابط الانضمام الخاص (القائمة البيضاء)")}</p>
      <p className="text-luxury-gray text-xs mb-3 leading-relaxed">{t("لا يدخل من هذا الرابط إلا الإيميلات التي تضيفينها هنا — انسخيه وشاركيه في مجموعة الواتساب بعد إضافة المشتركين")}</p>
      <div className="flex items-center gap-2">
        <code data-testid="join-link-url" dir="ltr" className="flex-1 text-luxury-bone text-sm bg-luxury-black border border-luxury-border px-3 py-2 rounded-lg truncate">{url}</code>
        <button type="button" data-testid="join-link-copy-button" onClick={() => { navigator.clipboard.writeText(url); toast.success(t("تم نسخ رابط الانضمام")); }}
          className="flex items-center gap-2 bg-luxury-gold text-luxury-black text-sm font-semibold px-4 py-2 rounded-full hover:bg-luxury-goldLight transition-colors whitespace-nowrap">
          <Copy className="w-4 h-4" strokeWidth={1.5} /> {t("نسخ الرابط")}
        </button>
      </div>
    </div>
  );
};

export const AccessTab = () => {
  const { t } = useAdminT();
  const [grants, setGrants] = useState([]);
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState({ email: "", course_id: "", days: 0, dur: "0" });
  const [lastLink, setLastLink] = useState(null);
  const [saving, setSaving] = useState(false);

  // ---- Client account with password ----
  const [acct, setAcct] = useState({ email: "", name: "", password: "", course_id: "", days: 0, dur: "0" });
  const [acctBusy, setAcctBusy] = useState(false);
  const [acctResult, setAcctResult] = useState(null);

  const genPassword = () => {
    const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789";
    const arr = new Uint32Array(10);
    crypto.getRandomValues(arr);
    return Array.from(arr, (n) => chars[n % chars.length]).join("");
  };

  const createAccount = async (e) => {
    e.preventDefault();
    setAcctBusy(true);
    try {
      const res = await axios.post(`${API}/admin/customers/create-account`, {
        email: acct.email, name: acct.name, password: acct.password,
        course_id: acct.course_id, days: Number(acct.days) || 0,
      }, { headers: authHeaders(), timeout: 60000 });
      setAcctResult({ email: res.data.email, course: res.data.course_title, password: acct.password, action: res.data.action });
      toast.success(res.data.action === "created" ? t("تم إنشاء الحساب ومنح الوصول") : t("حساب موجود — تم تحديث كلمة المرور ومنح الوصول"));
      setAcct({ email: "", name: "", password: "", course_id: "", days: 0, dur: "0" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAcctBusy(false);
    }
  };

  const acctText = acctResult
    ? `وصولك لدورة «${acctResult.course}»\nرابط الدخول: ${window.location.origin}/account\nالبريد: ${acctResult.email}\nكلمة المرور: ${acctResult.password}`
    : "";

  const load = () => {
    axios.get(`${API}/admin/access`, { headers: authHeaders() }).then((res) => setGrants(res.data)).catch(() => {});
    axios.get(`${API}/courses`).then((res) => setCourses(res.data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const grant = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/access`, { email: form.email, course_id: form.course_id, days: Number(form.days) || 0, origin_url: window.location.origin, send_email: sendEmail }, { headers: authHeaders(), timeout: 240000 });
      if (res.data.link) {
        setLastLink({ link: res.data.link });
        navigator.clipboard.writeText(res.data.link).catch(() => {});
      }
      toast.success(res.data.emailed ? t("تم منح الصلاحية وأُرسل رابط الدخول إلى بريد العميلة") : sendEmail ? t("تم منح الصلاحية — الإيميل سيتأخر قليلاً، والرابط منسوخ أدناه") : t("أُضيف للقائمة البيضاء بدون إيميل — الرابط منسوخ أدناه"));
      setForm({ email: "", course_id: "", days: 0, dur: "0" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm(t("إلغاء هذه الصلاحية؟"))) return;
    await axios.delete(`${API}/admin/access/${id}`, { headers: authHeaders() });
    toast.success(t("تم الإلغاء"));
    load();
  };

  const resend = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/access/${id}/resend`, { origin_url: window.location.origin }, { headers: authHeaders(), timeout: 240000 });
      if (res.data.link) {
        setLastLink({ link: res.data.link });
        navigator.clipboard.writeText(res.data.link).catch(() => {});
      }
      toast.success(res.data.sent ? t("أُعيد إرسال الرابط — ونُسخ أيضاً") : t("تعذر الإرسال البريدي الآن — نُسخ الرابط، أرسليه واتساب"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const active = (g) => !g.revoked && (!g.expires_at || g.expires_at > new Date().toISOString());

  // ---- Bulk import (Kajabi CSV) ----
  const [bulkCourse, setBulkCourse] = useState("");
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [sendEmail, setSendEmail] = useState(true);
  const [bulkDur, setBulkDur] = useState("0");
  const [bulkDays, setBulkDays] = useState(21);

  const parseRows = (rowsArrays) => {
    const rows = [];
    for (const cells of rowsArrays) {
      const clean = (cells || []).map((c) => String(c ?? "").trim().replace(/^["']+|["']+$/g, ""));
      const email = (clean.find((c) => c.includes("@")) || "").toLowerCase();
      if (!email) continue;
      const name = clean.find((c) => c && !c.includes("@")) || "";
      rows.push({ name, email });
    }
    const seen = new Set();
    return rows.filter((r) => (seen.has(r.email) ? false : (seen.add(r.email), true)));
  };

  const parseCsv = (text) =>
    parseRows(text.split(/\r?\n/).filter((l) => l.trim()).map((l) => l.split(/[,;\t]/)));

  const handleBulkFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isExcel = /\.(xlsx|xls)$/i.test(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      let rows = [];
      if (isExcel) {
        try {
          const wb = XLSX.read(reader.result, { type: "array" });
          const data = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: false });
          rows = parseRows(data);
        } catch {
          rows = [];
        }
      } else {
        rows = parseCsv(String(reader.result || ""));
      }
      setBulkRows(rows);
      setBulkResult(null);
      if (!rows.length) toast.error(t("لم يتم العثور على أي بريد إلكتروني في الملف"));
    };
    if (isExcel) reader.readAsArrayBuffer(file); else reader.readAsText(file);
  };

  const bulkGrant = async () => {
    if (!bulkCourse || !bulkRows.length) {
      toast.error(t("اختاري الدورة وارفعي الملف أولاً"));
      return;
    }
    setBulkBusy(true);
    try {
      const res = await axios.post(`${API}/admin/access/bulk`, { course_id: bulkCourse, origin_url: window.location.origin, people: bulkRows, days: bulkDur === "custom" ? (Number(bulkDays) || 0) : 0 }, { headers: authHeaders(), timeout: 300000 });
      setBulkResult(res.data);
      toast.success(res.data.emails_queued ? `${t("تم منح الوصول لـ")} ${res.data.granted} — ${t("الإيميلات تُرسل تباعاً في الخلفية بأمان")}` : `${t("تم منح الوصول لـ")} ${res.data.granted}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div data-testid="admin-access-tab">
      <h2 className="font-serif text-3xl text-luxury-bone mb-8">{t("صلاحيات الوصول للدورات")}</h2>

      <form data-testid="access-grant-form" onSubmit={grant} className="bg-luxury-surface border border-luxury-border p-8 mb-10 grid grid-cols-1 md:grid-cols-4 gap-5 items-end">
        <div>
          <label className="block text-sm text-luxury-gray mb-2">{t("بريد العميلة")}</label>
          <input data-testid="access-email-input" type="email" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} required />
        </div>
        <div>
          <label className="block text-sm text-luxury-gray mb-2">{t("الدورة")}</label>
          <select data-testid="access-course-select" value={form.course_id} onChange={(e) => {
            const cid = e.target.value;
            const c = courses.find((x) => x.id === cid);
            const d = Number(c?.access_days) || 0;
            setForm({ ...form, course_id: cid, dur: d > 0 ? "custom" : "0", days: d > 0 ? d : 0 });
          }} className={inputCls} required>
            <option value="">{t("— اختاري —")}</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm text-luxury-gray mb-2">{t("مدة الوصول")}</label>
          <div className="flex gap-2">
            <button type="button" data-testid="access-dur-lifetime" onClick={() => setForm({ ...form, dur: "0", days: 0 })}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-3 rounded-full text-sm font-semibold border transition-colors ${form.dur !== "custom" ? "bg-luxury-gold text-luxury-black border-luxury-gold" : "border-luxury-border text-luxury-gray hover:border-luxury-gold hover:text-luxury-gold"}`}>
              <InfinityIcon className="w-4 h-4" strokeWidth={1.5} /> {t("مدى الحياة")}
            </button>
            <button type="button" data-testid="access-dur-limited" onClick={() => setForm({ ...form, dur: "custom", days: Number(form.days) || 21 })}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-3 rounded-full text-sm font-semibold border transition-colors ${form.dur === "custom" ? "bg-luxury-gold text-luxury-black border-luxury-gold" : "border-luxury-border text-luxury-gray hover:border-luxury-gold hover:text-luxury-gold"}`}>
              <Clock className="w-4 h-4" strokeWidth={1.5} /> {t("فترة محددة")}
            </button>
          </div>
          {form.dur === "custom" && (
            <div className="flex items-center gap-2 mt-2">
              <input data-testid="access-days-input" type="number" min="1" dir="ltr" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} className={inputCls} placeholder={t("عدد الأيام")} />
              <span className="text-luxury-gray text-xs whitespace-nowrap">{t("يوم — يحددها المشرف فقط")}</span>
            </div>
          )}
        </div>
        <button data-testid="access-grant-button" type="submit" disabled={saving}
          className="flex items-center justify-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-6 py-3 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" strokeWidth={1.5} />}
          {t("منح الوصول")}
        </button>
        <label className="md:col-span-4 flex items-center gap-2 text-sm text-luxury-gray cursor-pointer select-none -mt-2">
          <input data-testid="access-send-email-checkbox" type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="accent-luxury-gold w-4 h-4" />
          {t("إرسال إيميل الوصول تلقائياً (ألغِيه للإضافة الصامتة للقائمة البيضاء — مثل مجموعة Magnetic)")}
        </label>
        <div className="md:col-span-4">
          <JoinLinkBox courseId={form.course_id} t={t} />
        </div>
      </form>

      {lastLink && (
        <div data-testid="access-last-link-box" className="border border-luxury-gold/50 bg-luxury-gold/5 p-4 rounded-xl mb-8 flex items-center gap-3 flex-wrap">
          <p className="text-luxury-gold text-sm font-semibold w-full">{t("رابط الدخول — نُسخ تلقائياً، أرسليه يدوياً (واتساب) عند الحاجة")}</p>
          <code data-testid="access-last-link" dir="ltr" className="flex-1 min-w-0 text-luxury-bone text-xs bg-luxury-black border border-luxury-border px-3 py-2.5 rounded-lg truncate">{lastLink.link}</code>
          <button data-testid="access-last-link-copy" onClick={() => { navigator.clipboard.writeText(lastLink.link); toast.success(t("تم النسخ")); }}
            className="flex items-center gap-2 bg-luxury-gold text-luxury-black text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-luxury-goldLight transition-colors whitespace-nowrap">
            <Copy className="w-4 h-4" strokeWidth={1.5} /> {t("نسخ")}
          </button>
          <button data-testid="access-last-link-close" onClick={() => setLastLink(null)} className="text-luxury-gray hover:text-luxury-bone text-xs">{t("إخفاء")}</button>
        </div>
      )}

      <div className="bg-luxury-surface border border-luxury-border p-8 mb-10" data-testid="account-create-section">
        <h3 className="font-serif text-2xl text-luxury-gold mb-2">{t("حساب عميلة بكلمة مرور")}</h3>
        <p className="text-luxury-gray text-sm mb-6">{t("أنشئي للعميلة حساب دخول كلاسيكي (بريد + كلمة مرور) مع وصول للدورة — تدخل من صفحة حسابي وتجد مكتبتها كاملة بدون روابط بريدية.")}</p>
        <form data-testid="account-create-form" onSubmit={createAccount} className="grid grid-cols-1 md:grid-cols-4 gap-5 items-end">
          <div>
            <label className="block text-sm text-luxury-gray mb-2">{t("بريد العميلة")}</label>
            <input data-testid="account-email-input" type="email" dir="ltr" value={acct.email} onChange={(e) => setAcct({ ...acct, email: e.target.value })} className={inputCls} required />
          </div>
          <div>
            <label className="block text-sm text-luxury-gray mb-2">{t("اسمها (اختياري)")}</label>
            <input data-testid="account-name-input" type="text" value={acct.name} onChange={(e) => setAcct({ ...acct, name: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm text-luxury-gray mb-2">{t("كلمة المرور")}</label>
            <div className="flex gap-2">
              <input data-testid="account-password-input" type="text" dir="ltr" value={acct.password} onChange={(e) => setAcct({ ...acct, password: e.target.value })} className={inputCls} required minLength={8} />
              <button type="button" data-testid="account-password-generate" onClick={() => setAcct({ ...acct, password: genPassword() })}
                className="border border-luxury-gold text-luxury-gold text-sm font-semibold px-4 rounded-full hover:bg-luxury-gold hover:text-luxury-black transition-colors whitespace-nowrap">
                {t("توليد")}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm text-luxury-gray mb-2">{t("الدورة")}</label>
            <select data-testid="account-course-select" value={acct.course_id} onChange={(e) => {
              const cid = e.target.value;
              const c = courses.find((x) => x.id === cid);
              const d = Number(c?.access_days) || 0;
              setAcct({ ...acct, course_id: cid, dur: d > 0 ? "custom" : "0", days: d > 0 ? d : 0 });
            }} className={inputCls} required>
              <option value="">{t("— اختاري —")}</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="block text-sm text-luxury-gray mb-2">{t("مدة الوصول")}</label>
            <div className="flex gap-2 items-center flex-wrap">
              <button type="button" data-testid="account-dur-lifetime" onClick={() => setAcct({ ...acct, dur: "0", days: 0 })}
                className={`flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${acct.dur !== "custom" ? "bg-luxury-gold text-luxury-black border-luxury-gold" : "border-luxury-border text-luxury-gray hover:border-luxury-gold hover:text-luxury-gold"}`}>
                <InfinityIcon className="w-4 h-4" strokeWidth={1.5} /> {t("مدى الحياة")}
              </button>
              <button type="button" data-testid="account-dur-limited" onClick={() => setAcct({ ...acct, dur: "custom", days: Number(acct.days) || 21 })}
                className={`flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${acct.dur === "custom" ? "bg-luxury-gold text-luxury-black border-luxury-gold" : "border-luxury-border text-luxury-gray hover:border-luxury-gold hover:text-luxury-gold"}`}>
                <Clock className="w-4 h-4" strokeWidth={1.5} /> {t("فترة محددة")}
              </button>
              {acct.dur === "custom" && (
                <div className="flex items-center gap-2">
                  <input data-testid="account-days-input" type="number" min="1" dir="ltr" value={acct.days} onChange={(e) => setAcct({ ...acct, days: e.target.value })} className={`${inputCls} !w-28`} placeholder={t("عدد الأيام")} />
                  <span className="text-luxury-gray text-xs whitespace-nowrap">{t("يوم")}</span>
                </div>
              )}
            </div>
          </div>
          <button data-testid="account-create-button" type="submit" disabled={acctBusy}
            className="flex items-center justify-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-6 py-3 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60">
            {acctBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" strokeWidth={1.5} />}
            {t("إنشاء الحساب")}
          </button>
        </form>
        {acctResult && (
          <div data-testid="account-result-box" className="border border-luxury-gold/50 bg-luxury-gold/5 p-4 rounded-xl mt-6">
            <p className="text-luxury-gold text-sm font-semibold mb-2">{t("بيانات دخول العميلة — انسخيها وأرسليها واتساب")}</p>
            <pre data-testid="account-result-text" className="text-luxury-bone text-sm bg-luxury-black border border-luxury-border px-4 py-3 rounded-lg whitespace-pre-wrap font-sans leading-relaxed">{acctText}</pre>
            <div className="flex items-center gap-3 mt-3">
              <button data-testid="account-result-copy" onClick={() => { navigator.clipboard.writeText(acctText); toast.success(t("تم النسخ")); }}
                className="flex items-center gap-2 bg-luxury-gold text-luxury-black text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-luxury-goldLight transition-colors">
                <Copy className="w-4 h-4" strokeWidth={1.5} /> {t("نسخ البيانات")}
              </button>
              <button data-testid="account-result-close" onClick={() => setAcctResult(null)} className="text-luxury-gray hover:text-luxury-bone text-xs">{t("إخفاء")}</button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-luxury-surface border border-luxury-border p-8 mb-10" data-testid="bulk-import-section">
        <h3 className="font-serif text-2xl text-luxury-gold mb-2">{t("استيراد جماعي من Kajabi (CSV)")}</h3>
        <p className="text-luxury-gray text-sm mb-6">{t("ارفعي ملف CSV المصدّر من Kajabi — سيُمنح كل عميل فيه وصول مدى الحياة للدورة المختارة ويصله رابط الدخول على بريده تلقائياً.")}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-end">
          <div>
            <label className="block text-sm text-luxury-gray mb-2">{t("الدورة التي اشتروها")}</label>
            <select data-testid="bulk-course-select" value={bulkCourse} onChange={(e) => {
              const cid = e.target.value;
              setBulkCourse(cid);
              const c = courses.find((x) => x.id === cid);
              const d = Number(c?.access_days) || 0;
              setBulkDur(d > 0 ? "custom" : "0");
              setBulkDays(d > 0 ? d : 21);
            }} className={inputCls}>
              <option value="">{t("— اختاري —")}</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-luxury-gray mb-2">{t("ملف العملاء (Excel أو CSV)")}</label>
            <input data-testid="bulk-file-input" type="file" accept=".csv,.txt,.xlsx,.xls,text/csv,text/plain" dir="ltr" onChange={handleBulkFile} className={inputCls} />
          </div>
          <button data-testid="bulk-grant-button" type="button" onClick={bulkGrant} disabled={bulkBusy || !bulkRows.length || !bulkCourse}
            className="flex items-center justify-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-6 py-3 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60">
            {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" strokeWidth={1.5} />}
            {t("منح الوصول للجميع")}{bulkRows.length ? ` (${bulkRows.length})` : ""}
          </button>
        </div>
        <div className="mt-5">
          <JoinLinkBox courseId={bulkCourse} t={t} />
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <span className="text-sm text-luxury-gray">{t("مدة الوصول")}:</span>
          <button type="button" data-testid="bulk-dur-lifetime" onClick={() => setBulkDur("0")}
            className={`flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${bulkDur !== "custom" ? "bg-luxury-gold text-luxury-black border-luxury-gold" : "border-luxury-border text-luxury-gray hover:border-luxury-gold hover:text-luxury-gold"}`}>
            <InfinityIcon className="w-4 h-4" strokeWidth={1.5} /> {t("مدى الحياة")}
          </button>
          <button type="button" data-testid="bulk-dur-limited" onClick={() => setBulkDur("custom")}
            className={`flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${bulkDur === "custom" ? "bg-luxury-gold text-luxury-black border-luxury-gold" : "border-luxury-border text-luxury-gray hover:border-luxury-gold hover:text-luxury-gold"}`}>
            <Clock className="w-4 h-4" strokeWidth={1.5} /> {t("فترة محددة للجميع")}
          </button>
          {bulkDur === "custom" && (
            <div className="flex items-center gap-2">
              <input data-testid="bulk-days-input" type="number" min="1" dir="ltr" value={bulkDays} onChange={(e) => setBulkDays(e.target.value)} className={`${inputCls} !w-28`} placeholder={t("عدد الأيام")} />
              <span className="text-luxury-gray text-xs whitespace-nowrap">{t("يوم — يحددها المشرف فقط")}</span>
            </div>
          )}
        </div>
        {bulkRows.length > 0 && (
          <div className="mt-5 border border-luxury-border p-4" data-testid="bulk-preview">
            <p className="text-luxury-gray text-sm mb-3">{t("سيتم منح الوصول لـ")} {bulkRows.length} {t("عميل")}:</p>
            <div className="space-y-1 text-sm" dir="ltr">
              {bulkRows.slice(0, 5).map((r, i) => (
                <p key={i} className="text-luxury-bone">{r.name ? `${r.name} — ` : ""}{r.email}</p>
              ))}
              {bulkRows.length > 5 && <p className="text-luxury-gray">… +{bulkRows.length - 5}</p>}
            </div>
          </div>
        )}
        {bulkResult && (
          <p className="mt-4 text-sm text-luxury-gold" data-testid="bulk-result">
            {t("تم منح الوصول لـ")} {bulkResult.granted} — {bulkResult.emails_queued ? t("الإيميلات تُرسل تباعاً في الخلفية بأمان") : `${t("أُرسلت")} ${bulkResult.emailed || 0} ${t("رسالة")}`}
            {bulkResult.failed?.length ? ` — ${t("تعذّر على")}: ${bulkResult.failed.join(", ")}` : ""}
          </p>
        )}
      </div>

      <div className="space-y-4">
        {grants.filter((g) => !g.revoked).map((g, i) => (
          <div key={g.id} data-testid={`access-row-${i}`} className="bg-luxury-surface border border-luxury-border p-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-luxury-bone font-semibold" dir="ltr">{g.email}</p>
              <p className="text-luxury-gray text-sm mt-1">
                {g.course_title} — {g.expires_at ? `${t("حتى")} ${g.expires_at.slice(0, 10)}` : t("مدى الحياة")} — {g.source === "stripe" ? t("شراء") : t("يدوي")}
                {!active(g) && <span className="text-red-400"> {t("— منتهية")}</span>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid={`access-resend-${i}`} onClick={() => resend(g.id)}
                className="flex items-center gap-2 border border-luxury-border text-luxury-gold px-4 py-2 rounded-full text-sm hover:border-luxury-gold transition-colors">
                <Mail className="w-4 h-4" strokeWidth={1.5} /> {t("إعادة إرسال الرابط")}
              </button>
              <button data-testid={`access-revoke-${i}`} onClick={() => revoke(g.id)}
                className="flex items-center gap-2 border border-luxury-border text-red-400 px-4 py-2 rounded-full text-sm hover:border-red-400 transition-colors">
                <Trash2 className="w-4 h-4" strokeWidth={1.5} /> {t("إلغاء")}
              </button>
            </div>
          </div>
        ))}
        {grants.filter((g) => !g.revoked).length === 0 && (
          <p className="text-luxury-gray text-center py-10">{t("لا توجد صلاحيات بعد")}</p>
        )}
      </div>
    </div>
  );
};
